import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import { CollaboratorRole } from '@application/enums/collaborator-role';
import { CollaboratorDocumentType } from '@application/enums/collaborator-document-type';
import { compressImageTo1MB } from '@application/helpers/file-helper';
import { Box, Button, Typography, IconButton, useTheme, Divider, alpha, Tooltip, Checkbox } from '@mui/material';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useAddNewUserStyles from '@ui/pages/Users/AddEditUser/useAddEditUserStyles';
import ImageUpload from '@ui/pages/Users/AddEditUser/components/ImageUpload';
import { useSelector, useDispatch } from 'react-redux';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { Icon } from '@iconify/react';
import { getAllLanguagesAsync } from '@application/features/languages/languagesThunk';
import { filterApplicationLanguages } from '@application/helpers/language-filter-helper';
const getCompanyRegionGroups = (companies) => {
    const groups = {};
    companies.forEach((company) => {
        const region = company.regionLabel || 'Other';
        if (!groups[region]) {
            groups[region] = [];
        }
        groups[region].push(company);
    });
    return groups;
};
const AddEditCollaborator = forwardRef(({ onSubmit, onCancel, dialog = false, edit = false, collaborator }, ref) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const initialized = useRef(false);
    const styles = useAddNewUserStyles();
    const prevCollaboratorId = useRef();
    const companies = useSelector((state) => state.company.companies);
    const companiesByRegion = useSelector((state) => state.company.companiesByRegion);
    const languages = useSelector((state) => state.languages.languages);
    const companyRegionLookup = useMemo(() => {
        const lookup = {};
        companiesByRegion?.forEach((region) => {
            region.companies.forEach((company) => {
                lookup[company.id] = region.regionLabel;
            });
        });
        return lookup;
    }, [companiesByRegion]);
    const companyOptions = companies.map((c) => ({
        label: c.name,
        value: c.id,
        regionLabel: companyRegionLookup[c.id] || 'Other'
    }));
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    // State for multi-language NDA documents
    const [ndaDocuments, setNdaDocuments] = useState(new Map());
    const [existingNdaDocuments, setExistingNdaDocuments] = useState(new Map());
    const [ndaError, setNdaError] = useState('');
    const defaultValues = edit && collaborator
        ? {
            firstName: collaborator.firstName,
            lastName: collaborator.lastName,
            email: collaborator.email,
            phoneNo: collaborator.phoneNo,
            description: collaborator.description,
            type: collaborator.type,
            role: collaborator.role ?? CollaboratorRole.Collaborator,
            accessLevel: collaborator.accessLevel,
            category: collaborator.category,
            profilePicture: collaborator.profilePictureUrl || '',
            companyIds: [],
            collaboratingCompany: collaborator.collaboratingCompany || ''
        }
        : {
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: '',
            description: '',
            type: 0,
            role: CollaboratorRole.Collaborator,
            accessLevel: null,
            category: '',
            profilePicture: '',
            companyIds: [],
            collaboratingCompany: ''
        };
    const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        defaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: false
    });
    useEffect(() => {
        if (edit && collaborator && collaborator.id !== prevCollaboratorId.current) {
            initialized.current = false;
        }
    }, [edit, collaborator]);
    useEffect(() => {
        if (edit && collaborator && collaborator.companyIds && companyOptions?.length) {
            const selected = companyOptions.filter((opt) => collaborator.companyIds?.includes(opt.value));
            setValue('companyIds', selected);
        }
    }, [edit, collaborator, companyOptions]);
    useEffect(() => {
        if (edit && collaborator && collaborator.id !== prevCollaboratorId.current) {
            reset({
                firstName: collaborator.firstName,
                lastName: collaborator.lastName,
                email: collaborator.email,
                phoneNo: collaborator.phoneNo,
                description: collaborator.description,
                type: collaborator.type,
                role: collaborator.role ?? CollaboratorRole.Collaborator,
                accessLevel: collaborator.accessLevel,
                category: collaborator.category,
                profilePicture: collaborator.profilePictureUrl || '',
                companyIds: [],
                collaboratingCompany: collaborator.collaboratingCompany || ''
            });
            prevCollaboratorId.current = collaborator.id;
        }
        else if (!edit && (!collaborator || collaborator.id !== prevCollaboratorId.current)) {
            reset({
                firstName: '',
                lastName: '',
                email: '',
                phoneNo: '',
                description: '',
                type: 0,
                role: CollaboratorRole.Collaborator,
                accessLevel: null,
                category: '',
                profilePicture: '',
                companyIds: [],
                collaboratingCompany: ''
            });
            prevCollaboratorId.current = collaborator?.id;
        }
    }, [edit, collaborator, reset]);
    // Load languages on mount
    useEffect(() => {
        if (!languages || languages.length === 0) {
            dispatch(getAllLanguagesAsync());
        }
    }, [dispatch, languages]);
    useEffect(() => {
        // Set existing NDA documents when editing
        if (edit && collaborator) {
            // Reset states when collaborator changes
            setNdaDocuments(new Map());
            setNdaError('');
            if (collaborator.collaboratorDocuments && collaborator.collaboratorDocuments.length > 0) {
                const ndaDocs = collaborator.collaboratorDocuments.filter((doc) => doc.type === CollaboratorDocumentType.NonDisclosureAgreement);
                const existingMap = new Map();
                ndaDocs.forEach((doc) => {
                    if (doc.languageId) {
                        existingMap.set(doc.languageId, { name: doc.name, url: doc.url });
                    }
                });
                setExistingNdaDocuments(existingMap);
            }
            else {
                setExistingNdaDocuments(new Map());
            }
        }
    }, [edit, collaborator?.id, collaborator?.collaboratorDocuments]);
    const handleFormSubmit = async (data) => {
        if (data.accessLevel === null) {
            return;
        }
        if (data.type === null || data.type === undefined) {
            return;
        }
        // Validate NDA documents are required for all languages (filtered to exclude SV)
        const filteredLanguages = filterApplicationLanguages(languages);
        const totalNdaDocuments = ndaDocuments.size + existingNdaDocuments.size;
        const totalLanguages = filteredLanguages.length || 0;
        if (totalNdaDocuments < totalLanguages) {
            setNdaError(t('collaborators.ndaAllLanguagesRequired'));
            return;
        }
        let profilePicturePayload = undefined;
        if (data.profilePicture) {
            if (typeof data.profilePicture === 'string' && data.profilePicture.trim() !== '') {
                // In edit mode, if it's a string (existing URL), only send if it's a new File
                // Otherwise, don't include it (backend will keep existing)
                profilePicturePayload = undefined;
            }
            else if (data.profilePicture instanceof File) {
                profilePicturePayload = await compressImageTo1MB(data.profilePicture);
            }
        }
        if (edit && collaborator) {
            // Create UpdateCollaboratorRequest for edit mode
            const payload = {
                id: collaborator.id,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNo: data.phoneNo,
                description: data.description,
                type: data.type,
                role: data.role,
                accessLevel: data.accessLevel,
                category: data.category,
                collaboratingCompany: data.collaboratingCompany,
                userId: collaborator.userId || null,
                isEnabled: collaborator.isEnabled,
                companyIds: data.companyIds.map((c) => c.value)
            };
            // Only include profilePictureUrl if it's a new File
            if (profilePicturePayload instanceof File) {
                payload.profilePictureUrl = profilePicturePayload;
            }
            // Include NDA documents if uploaded
            if (ndaDocuments.size > 0) {
                payload.collaboratorDocuments = Array.from(ndaDocuments.entries()).map(([languageId, file]) => ({
                    file,
                    type: CollaboratorDocumentType.NonDisclosureAgreement,
                    languageId
                }));
            }
            onSubmit?.(payload);
        }
        else {
            // Create CreateCollaboratorRequest for create mode
            const payload = {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNo: data.phoneNo,
                description: data.description,
                type: data.type,
                role: data.role,
                accessLevel: data.accessLevel,
                category: data.category,
                collaboratingCompany: data.collaboratingCompany,
                companyIds: data.companyIds.map((c) => c.value)
            };
            // Only include profilePictureUrl if it has a value
            if (profilePicturePayload !== undefined) {
                payload.profilePictureUrl = profilePicturePayload;
            }
            // Include NDA documents if uploaded
            if (ndaDocuments.size > 0) {
                payload.collaboratorDocuments = Array.from(ndaDocuments.entries()).map(([languageId, file]) => ({
                    file,
                    type: CollaboratorDocumentType.NonDisclosureAgreement,
                    languageId
                }));
            }
            onSubmit?.(payload);
        }
    };
    useImperativeHandle(ref, () => ({
        submitForm: () => {
            handleSubmit(handleFormSubmit)();
        }
    }));
    const accessLevelOptions = [
        { label: t('collaborators.accessLevelAll'), value: CollaboratorAccessLevel.All },
        { label: t('collaborators.accessLevelExternal'), value: CollaboratorAccessLevel.External }
    ];
    const roleOptions = [
        { label: t('collaborators.roleCollaborator'), value: CollaboratorRole.Collaborator },
        { label: t('collaborators.roleTransportManager'), value: CollaboratorRole.TransportManager }
    ];
    return (_jsxs(Box, { sx: { ...styles.main }, children: [_jsx(ImageUpload, { control: control, edit: edit, name: 'profilePicture', rules: {
                    validate: (value) => {
                        if (!value)
                            return true; // Optional field
                        if (typeof value === 'string')
                            return true; // Existing image URL (edit mode)
                        if (value instanceof File) {
                            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
                            if (!allowedTypes.includes(value.type.toLowerCase())) {
                                return t('users.fileTypeError');
                            }
                            if (value.size > 2 * 1024 * 1024) {
                                return t('users.fileSizeError');
                            }
                        }
                        return true;
                    }
                } }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'firstName', label: t('collaborators.firstName'), rules: {
                    required: t('validations.required', {
                        field: t('collaborators.firstName')
                    })
                }, errorMessage: errors.firstName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'lastName', label: t('collaborators.lastName'), rules: {
                    required: t('validations.required', {
                        field: t('collaborators.lastName')
                    })
                }, errorMessage: errors.lastName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'collaboratingCompany', label: t('collaborators.collaboratingCompany'), rules: {
                    required: t('validations.required', {
                        field: t('collaborators.collaboratingCompany')
                    })
                }, errorMessage: errors.collaboratingCompany?.message }), _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, label: t('users.company'), name: 'companyIds', showSelectAll: true, isSortable: false, selectAllLabel: t('actions.selectAll'), rules: {
                    required: t('validations.required', { field: t('users.company') })
                }, error: errors.companyIds?.message, options: companyOptions, groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                    const region = params.group;
                    const companiesInRegion = regionGroups[region] || [];
                    const selectedCompanies = control._formValues.companyIds || [];
                    const selectedInRegion = companiesInRegion.filter((c) => selectedCompanies.some((s) => s.value === c.value));
                    const allSelected = selectedInRegion.length === companiesInRegion.length;
                    const someSelected = selectedInRegion.length > 0 && !allSelected;
                    const isUngrouped = region === '' || region === 'Other';
                    const handleRegionToggle = () => {
                        const currentSelected = control._formValues.companyIds || [];
                        let newSelected;
                        if (allSelected) {
                            newSelected = currentSelected.filter((s) => !companiesInRegion.some((c) => c.value === s.value));
                        }
                        else {
                            const toAdd = companiesInRegion.filter((c) => !currentSelected.some((s) => s.value === c.value));
                            newSelected = [...currentSelected, ...toAdd];
                        }
                        reset({
                            ...control._formValues,
                            companyIds: newSelected
                        });
                    };
                    return (_jsxs("li", { children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    py: 0.5,
                                    cursor: isUngrouped ? 'default' : 'pointer'
                                }, onClick: !isUngrouped ? handleRegionToggle : undefined, children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 1, pt: 1, pb: 0.5, color: 'text.secondary' }, children: region }), !isUngrouped && (_jsx(Checkbox, { checked: allSelected, indeterminate: someSelected, sx: { mr: 1, p: 0 }, size: 'small' }))] }), _jsx("ul", { style: { padding: 0 }, children: params.children })] }, params.key));
                } }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'email', label: t('collaborators.email'), rules: {
                    required: t('validations.required', {
                        field: t('collaborators.email')
                    }),
                    pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('validations.invalidEmail', {
                            field: t('collaborators.email')
                        })
                    }
                }, errorMessage: errors.email?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'description', label: t('collaborators.description'), errorMessage: errors.description?.message, multiline: true, rows: 3 }), _jsx(SingleSelect, { bg: 'grey8', control: control, label: t('collaborators.role'), name: 'role', options: roleOptions, error: errors.role?.message, rules: {
                    required: t('validations.required', {
                        field: t('collaborators.role')
                    })
                } }), _jsx(SingleSelect, { bg: 'grey8', control: control, label: t('collaborators.accessLevel'), name: 'accessLevel', options: accessLevelOptions, error: errors.accessLevel?.message, disabled: edit && collaborator ? collaborator.canChangeAccessLevel === false : false, rules: {
                    required: t('validations.required', {
                        field: t('collaborators.accessLevel')
                    })
                } }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'category', label: t('collaborators.category'), rules: {
                    required: t('validations.required', {
                        field: t('collaborators.category')
                    })
                }, errorMessage: errors.category?.message }), _jsx(PhoneInputMasking, { bg: 'grey8', control: control, label: t('collaborators.phoneNumber'), name: 'phoneNo', error: errors.phoneNo?.message, rules: {
                    required: t('validations.required', {
                        field: t('collaborators.phoneNumber')
                    }),
                    validate: (value) => {
                        const phoneRegex = /^\d{8,}$/;
                        if (!phoneRegex.test(value || '')) {
                            return t('validations.invalidPhone', {
                                field: t('collaborators.phoneNumber')
                            });
                        }
                        return true;
                    }
                } }), _jsxs(Box, { sx: { mt: 2 }, children: [_jsx(Typography, { variant: 'subtitle2', sx: { mb: 0.5 }, children: t('collaborators.ndaDocument') || 'Upload NDA Document' }), _jsx(Typography, { variant: 'body2', sx: { mb: 2, color: theme.palette.text.disabled }, children: t('collaborators.ndaUploadDescription') || 'Select a PDF file to upload' }), languages && languages.length > 0 ? (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: filterApplicationLanguages(languages).map((language) => {
                            const hasNewFile = ndaDocuments.has(language.id);
                            const hasExistingFile = existingNdaDocuments.has(language.id);
                            const existingDoc = existingNdaDocuments.get(language.id);
                            const newFile = ndaDocuments.get(language.id);
                            const hasFile = hasNewFile || hasExistingFile;
                            return (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    padding: 1,
                                    border: ndaError
                                        ? `1px solid ${theme.palette.error.main}`
                                        : `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                                    borderRadius: '14px',
                                    backgroundColor: 'transparent',
                                    gap: 1
                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 81 }, children: [_jsx(Box, { component: 'img', src: language.flagUrl || '/assets/images/flags/empty-flag.svg', alt: language.name, sx: {
                                                    width: 24,
                                                    height: 18,
                                                    objectFit: 'cover',
                                                    borderRadius: 0.5
                                                } }), _jsx(Typography, { variant: 'body2', fontWeight: 400, children: language.name })] }), _jsx(Divider, { orientation: 'vertical', flexItem: true, sx: {
                                            borderColor: theme.palette.grey[400],
                                            borderWidth: '1px',
                                            alignSelf: 'stretch'
                                        } }), _jsx(Box, { sx: { flex: 1 }, children: !hasFile ? (_jsx(Box, { sx: {
                                                width: '100%',
                                                borderRadius: 1.5,
                                                overflow: 'hidden',
                                                cursor: collaborator?.isNDASigned ? 'not-allowed' : 'pointer',
                                                backgroundColor: theme.palette.common.grey8,
                                                border: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    borderColor: collaborator?.isNDASigned
                                                        ? alpha(theme.palette.grey[500], 0.2)
                                                        : theme.palette.primary.main,
                                                    backgroundColor: collaborator?.isNDASigned
                                                        ? theme.palette.common.grey8
                                                        : alpha(theme.palette.primary.main, 0.05)
                                                }
                                            }, onClick: () => {
                                                if (!collaborator?.isNDASigned) {
                                                    const input = document.getElementById(`nda-input-${language.id}`);
                                                    input?.click();
                                                }
                                            }, children: _jsx(Box, { sx: {
                                                    padding: 2.5,
                                                    textAlign: 'center'
                                                }, children: _jsx(Typography, { variant: 'body2', sx: {
                                                        color: collaborator?.isNDASigned
                                                            ? theme.palette.grey[400]
                                                            : theme.palette.text.disabled,
                                                        fontWeight: 400
                                                    }, children: t('fileUpload.uploadFile') || 'Upload file' }) }) })) : (_jsxs(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: 1,
                                                borderRadius: 1
                                            }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Icon, { icon: 'solar:document-bold', fontSize: 22, color: theme.palette.error.main }), _jsx(Typography, { variant: 'body2', fontWeight: 400, color: theme.palette.text.primary, children: newFile?.name || existingDoc?.name })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [!hasNewFile && hasExistingFile && existingDoc && (_jsx(Tooltip, { title: t('actions.view') || 'View', children: _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    window.open(existingDoc.url, '_blank');
                                                                }, sx: { border: 'none', padding: 0.5 }, children: _jsx(Icon, { icon: 'solar:eye-bold', width: '18', height: '18' }) }) })), _jsx(IconButton, { size: 'small', color: 'default', disabled: collaborator?.isNDASigned, onClick: (e) => {
                                                                e.stopPropagation();
                                                                if (hasNewFile) {
                                                                    setNdaDocuments((prev) => {
                                                                        const newMap = new Map(prev);
                                                                        newMap.delete(language.id);
                                                                        return newMap;
                                                                    });
                                                                }
                                                                if (hasExistingFile) {
                                                                    setExistingNdaDocuments((prev) => {
                                                                        const newMap = new Map(prev);
                                                                        newMap.delete(language.id);
                                                                        return newMap;
                                                                    });
                                                                }
                                                            }, sx: { border: 'none', padding: 0.5 }, children: _jsx(Icon, { icon: 'eva:close-fill', width: '18', height: '18' }) })] })] })) }), _jsx("input", { id: `nda-input-${language.id}`, type: 'file', accept: '.pdf,application/pdf', onChange: (e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                // Validate file type
                                                if (file.type !== 'application/pdf' &&
                                                    !file.name.toLowerCase().endsWith('.pdf')) {
                                                    setNdaError(t('validations.invalidFileType', { field: 'PDF' }) ||
                                                        'Only PDF files are allowed');
                                                    e.target.value = '';
                                                    return;
                                                }
                                                setNdaDocuments((prev) => new Map(prev).set(language.id, file));
                                                setNdaError('');
                                                e.target.value = '';
                                            }
                                        }, style: { display: 'none' }, disabled: collaborator?.isNDASigned })] }, language.id));
                        }) })) : (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('collaborators.loadingLanguages') })), ndaError && (_jsx(Typography, { variant: 'caption', sx: { mt: 0.5, color: theme.palette.error.main }, children: ndaError }))] }), !dialog && (_jsxs(Box, { sx: styles.actions, children: [_jsx(Button, { fullWidth: true, onClick: onCancel, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', onClick: handleSubmit(handleFormSubmit), color: 'inherit', size: 'large', children: edit ? t('actions.editNow') : t('actions.addNow') })] }))] }));
});
AddEditCollaborator.displayName = 'AddEditCollaborator';
export default AddEditCollaborator;
